import { HOME_TITLE, HOME_DESCRIPTION, SITE_ORIGIN, resolveSiteOrigin } from './homepageContent';

export const SITE_NAME = 'APDoneRight';
export const FALLBACK_SITE_ORIGIN = SITE_ORIGIN;

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);

const VITE_ENV = {
  VITE_SITE_ORIGIN: import.meta.env.VITE_SITE_ORIGIN,
  VITE_SITE_INDEXING_ENABLED: import.meta.env.VITE_SITE_INDEXING_ENABLED,
};

const readEnv = (key) => {
  try {
    const value = VITE_ENV[key];
    if (typeof value === 'string') return value;
  } catch (_error) {
    // Fall through to process.env in non-Vite contexts.
  }

  try {
    const value = process?.env?.[key];
    return typeof value === 'string' ? value : '';
  } catch (_error) {
    return '';
  }
};

const readBooleanFlag = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  return TRUE_VALUES.has(normalized);
};

const ENV_SITE_ORIGIN = resolveSiteOrigin(
  readEnv('VITE_SITE_ORIGIN') || readEnv('REACT_APP_SITE_ORIGIN') || readEnv('SITE_ORIGIN') || ''
);

const ENV_SITE_INDEXING_ENABLED = readBooleanFlag(
  readEnv('VITE_SITE_INDEXING_ENABLED') || readEnv('SITE_INDEXING_ENABLED') || ''
);

const getCanonicalHost = (origin) => {
  if (!origin) return new URL(FALLBACK_SITE_ORIGIN).host;
  try {
    return new URL(origin).host;
  } catch (error) {
    return new URL(FALLBACK_SITE_ORIGIN).host;
  }
};

export const CANONICAL_HOST = getCanonicalHost(ENV_SITE_ORIGIN);
export const CANONICAL_ORIGIN = ENV_SITE_ORIGIN || FALLBACK_SITE_ORIGIN;

export const DEFAULT_SITE_ORIGIN = (() => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      const metaOrigin = document
        ?.querySelector('meta[name="site-origin"]')
        ?.getAttribute('content');
      const originFromMeta = String(metaOrigin || '').trim();
      if (originFromMeta) return originFromMeta.replace(/\/+$/, '');
    } catch (err) {
      console.warn('[siteMeta] Failed to read meta site-origin:', err);
    }

    return window.location.origin.replace(/\/+$/, '');
  }

  const raw = ENV_SITE_ORIGIN;
  const origin = raw.replace(/\/+$/, '');
  return origin || FALLBACK_SITE_ORIGIN;
})();

export const SITE_INDEXING_ENABLED = (() => {
  if (ENV_SITE_INDEXING_ENABLED !== null) {
    return ENV_SITE_INDEXING_ENABLED;
  }

  if (typeof document !== 'undefined') {
    try {
      const raw = document
        ?.querySelector('meta[name="site-indexing-enabled"]')
        ?.getAttribute('content');
      const value = readBooleanFlag(raw);
      if (value !== null) return value;
    } catch (err) {
      console.warn('[siteMeta] Failed to read indexing meta flag:', err);
    }
  }

  return true;
})();

export const DEFAULT_TITLE = HOME_TITLE;

export const DEFAULT_DESCRIPTION = HOME_DESCRIPTION;

export const DEFAULT_SOCIAL_IMAGE_PATH = '/social-card.png';

export const resolveRobotsDirective = (value = 'index,follow') => {
  const normalized = String(value || 'index,follow')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
  return normalized || 'index, follow';
};

export const toAbsoluteUrl = (pathOrUrl) => {
  if (!pathOrUrl) return DEFAULT_SITE_ORIGIN;
  const value = String(pathOrUrl).trim();
  if (!value) return DEFAULT_SITE_ORIGIN;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${DEFAULT_SITE_ORIGIN}${path}`;
};

export const buildDocumentTitle = (title) => {
  const raw = String(title || '').trim();
  if (!raw) return DEFAULT_TITLE;
  const lower = raw.toLowerCase();
  const siteLower = SITE_NAME.toLowerCase();
  if (lower.includes(siteLower)) return raw;
  return `${raw} | ${SITE_NAME}`;
};
